import "./style.scss";

import {
    Avatar,
    Box,
    Collapse,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    tableClasses,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import ActionArrowRight from "assets/img/svg/actionArrowRight";
import variables from "assets/styles/_variables.module.scss";
import { useDebouncedCallback } from "hooks/useDebounce";
import usePagination from "hooks/usePagination";
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";

import EmptyState from "../EmptyState";
import Pagination from "../Pagination";
import ScrollShadowWrapper from "../ScrollShadowWrapper";
import StyledCheckbox from "../StyledCheckbox";
import type { LastElementTableProps } from "./type";

const tHeadStyles = {
    maxWidth: 160,
    fontWeight: 400,
    width: "fit-content",
    fontSize: "12px",
    color: "var(--foreground)",
    padding: "8px 16px",
    backgroundColor: "#F6F6F6",
    fontFamily: "'Poppins', sans-serif",
    lineHeight: "1rem",
};

const tBodyStyle = {
    fontWeight: 400,
    fontSize: "0.75rem",
    fontFamily: "'Poppins', sans-serif",
    borderColor: "#eaeaea",
};

export interface LastElementTableRef {
    changeMaxRows: (value: number) => void;
    /** Replaces the selection (selectable tables), e.g. "select all outdated rows" */
    setSelectedRows: (ids: any[]) => void;
}

/**
 * At first, this component was used to display the last 3 publications, but ended up being used as a generic table component
 * Some props are very specific and will likely not be used in other cases
 * And some other props need to be tweaked to lower the complexity of the component
 * The parameters for the columns can be seen in the type file relative to the component file
 */
const LastElementTable = forwardRef<any, LastElementTableProps>(function LastElementTable(
    props: LastElementTableProps,
    ref,
) {
    const {
        columns,
        data,
        onShowAll = () => {},
        type,
        max = 3,
        dataType,
        stickyFooter = false,
        stickyHeader = false,
        onRowClick,
        onRowClickId,
        onRowClickId2,
        collapseOn,
        collapseContent,
        isLoading = false,
        loadingRowsCount = 4,
        bottomFooterContent,
        maxHeight,
        selectable = false,
        onSelectRows = () => {},
        isNewDate,
        separatorContent,
        onScrollEnd,
        avatarSourceValue,
        isMobile,
        renderMobileRow,
        tableHeadStyles,
        tableBodyStyles,
        collapseSelectedBackground,
        collapseContentBackground,
        emptyStateAction,
        dataCount,
        APIData,
        allowInfiniteScroll = false,
        defaultRowId,
        minHeight,
        columnsNumber,
        portalType,
        tableClassName,
        rowClassName,
    } = props;
    const [t] = useTranslation();
    const [maxRows, setMaxRows] = useState(max);
    const [normalizedData, setNormalizedData] = useState<any[] | null>(null);
    const [dataToMap, setDataToMap] = useState<any[] | null>(null);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    const isPaginated = !allowInfiniteScroll && max <= -1;
    const useAPIPagination = useMemo(() => {
        return APIData ? isPaginated : false;
    }, [isPaginated, APIData]);

    useImperativeHandle(
        ref,
        (): LastElementTableRef => ({
            /**
             * Updates maxRows, specifically when clicking on See All button.
             */
            changeMaxRows: (value) => {
                setMaxRows(value);
            },
            setSelectedRows: (ids) => {
                setSelectedRows(ids);
                onSelectRows(ids);
            },
        }),
    );

    const [
        dividedByPage,
        handleChangePage,
        handleChangeRowsPerPage,
        page,
        rowsPerPage,
        ,
        ,
        orderAsc,
        orderBy,
        handleSort,
    ] = usePagination(normalizedData as any);

    const tableEl = useRef<HTMLDivElement | null>(null);
    const stickyStyle = (side: "left" | "right"): React.CSSProperties => ({
        position: "sticky",
        [side]: 0,
        zIndex: 10,
        backgroundColor: "#FAFAFA",
        [`border${side == "left" ? "Right" : "Left"}`]: "var(--border)",
    });

    /**
     * Starts by setting normalizedData which contains all rows regardless of pagination.
     */
    useEffect(() => {
        if (!data) return;
        if (isPaginated) {
            setNormalizedData(data); // If not using APIData, we don't set dataToMap as this will be done in following useEffect (other triggers).
            useAPIPagination && setDataToMap(data);
        } else {
            // When no pagination:
            if (allowInfiniteScroll) {
                // Copy data as is, because we display everything.
                setNormalizedData(data);
                setDataToMap(data);
            } else {
                // Otherwise, we only show until max row allowed.
                setNormalizedData(data.slice(0, max));
                setDataToMap(data.slice(0, max));
            }
        }
    }, [data]);

    /**
     * When they use pagination, set dataToMap depending on
     * whether they've used orderBy or not.
     * Need to keep orderAsc and page as dependencies, otherwise the table isn't correctly updated.
     */
    useEffect(() => {
        if (useAPIPagination || !normalizedData) return;

        if (!orderBy) {
            setDataToMap(isPaginated ? dividedByPage : normalizedData);
        } else {
            const sortedData = sortPaginatedDataOnFirstLoad(isPaginated ? dividedByPage : normalizedData);
            setDataToMap(sortedData);
        }
    }, [normalizedData, orderBy, orderAsc, page]);

    const sortPaginatedDataOnFirstLoad = (data) => {
        const sortedData = [...data].sort((a, b) => {
            const valueA = a[orderBy] ?? "";
            const valueB = b[orderBy] ?? "";

            // Sort strings & numbers
            if (typeof valueA === "string" && typeof valueB === "string") {
                return orderAsc === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            // Numbers or conversion
            const numA = Number(valueA);
            const numB = Number(valueB);
            return orderAsc === "asc" ? numA - numB : numB - numA;
        });

        return sortedData;
    };

    const handleSelectAll = () => {
        if (selectedRows.length == normalizedData?.length) {
            setSelectedRows([]);
            onSelectRows([]);
        } else {
            const ids = normalizedData?.map((row: any) => row.id) ?? [];
            setSelectedRows(ids ?? []);
            onSelectRows(ids ?? []);
        }
    };

    const handleSelectRow = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
        if (e.target.checked) {
            setSelectedRows((prev) => {
                onSelectRows([...prev, id]);
                return [...prev, id];
            });
        } else {
            setSelectedRows((prev) => {
                const newRows = prev.filter((item) => id !== item);
                onSelectRows(newRows);
                return newRows;
            });
        }
    };

    const scrollListener = useCallback(() => {
        if (!tableEl.current) return;
        if (!onScrollEnd) return;
        const bottom = tableEl.current.scrollHeight - tableEl.current.clientHeight;

        if (tableEl.current.scrollTop > bottom - 10) {
            onScrollEnd();
        }
    }, [onScrollEnd]);

    useLayoutEffect(() => {
        if (!tableEl.current) return;
        if (!onScrollEnd) return;
        const tableRef = tableEl.current;
        tableRef.addEventListener("scroll", scrollListener);
        return () => {
            tableRef.removeEventListener("scroll", scrollListener);
        };
    }, [scrollListener]);

    /**
     * Row we want to scroll to on render.
     */
    const referenceRow = defaultRowId ? document.getElementById(defaultRowId) : null;
    const savedPosition = localStorage.getItem("paymentTableScrollPosition");

    // Resets saved position to 0 when first loading data otherwise old value is used.
    useEffect(() => {
        if (isLoading) {
            localStorage.setItem("paymentTableScrollPosition", "0");
        }
    }, [isLoading]);

    /**
     * Saves scroll position.
     */
    const saveScrollPosition = useDebouncedCallback(() => {
        if (!tableEl.current || !allowInfiniteScroll) return;
        const topScrollPosition = tableEl?.current?.scrollTop;
        localStorage.setItem("paymentTableScrollPosition", topScrollPosition.toString());
    }, 500);

    /**
     * Sets scroll position.
     */
    const setTopPosition = () => {
        const timeoutId = setTimeout(() => {
            if (savedPosition && tableEl.current) {
                tableEl.current.scrollTop = +savedPosition;
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    };

    // On first render, this scrolls to current year, otherwise it moves to saved scroll position --> scroll needs to be enabled (aka no pagination)
    useEffect(() => {
        if (isLoading || !allowInfiniteScroll) return;
        if (savedPosition && savedPosition === "0" && referenceRow) {
            // Need to scroll to previous sibling to have a good alignment.
            referenceRow?.previousElementSibling?.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (savedPosition && +savedPosition > 0) {
            setTopPosition();
        }
    }, [isLoading, referenceRow, savedPosition]);

    /**
     * Fake array to display several rows with Skeleton on loading.
     */
    const loadingTable = useMemo(
        () => Array.from({ length: loadingRowsCount }, (_, index) => index),
        [loadingRowsCount],
    );
    const loadingTableColumns = [...Array(columnsNumber ?? columns.length).keys()];

    return (
        <div className={`last-element-table ${tableClassName}`}>
            {isMobile && renderMobileRow && (
                <div className="table-list-mobile">
                    {data.map((element, i) => (
                        <div key={`last-element-${element.id}`}>
                            {isNewDate &&
                                isNewDate(element, i, data) &&
                                separatorContent &&
                                separatorContent(element, i)}
                            {
                                <div
                                    className="table-list-mobile-row"
                                    onClick={() =>
                                        onRowClick
                                            ? onRowClick(
                                                  element[onRowClickId ?? 0] ?? element.id,
                                                  onRowClickId2 ? element[onRowClickId2 ?? 1] : "",
                                                  element.type ?? "title",
                                                  element,
                                              )
                                            : {}
                                    }
                                >
                                    {renderMobileRow(element)}
                                </div>
                            }
                        </div>
                    ))}
                </div>
            )}
            {!isMobile && (
                <Paper
                    sx={{
                        width: "100%",
                        // overflow: "hidden",
                        border: `1px solid ${variables["border-grey"]}`,
                        borderRadius: "4px",
                        boxShadow: "none",
                    }}
                >
                    {/* <TableContainer sx={ isPaginated ? { maxHeight: 300 } : {}}> */}
                    <ScrollShadowWrapper className={allowInfiniteScroll ? "active" : ""} style={{ paddingRight: "0" }}>
                        <TableContainer style={{ maxHeight, minHeight }} ref={tableEl} onScroll={saveScrollPosition}>
                            <Table stickyHeader={stickyHeader}>
                                <TableHead>
                                    <TableRow>
                                        {selectable && (
                                            <TableCell
                                                sx={{ ...(tableHeadStyles ?? tHeadStyles), maxWidth: 42, zIndex: 3 }}
                                                className="with-checkbox"
                                                align="center"
                                            >
                                                <StyledCheckbox
                                                    className="last-element-table-selectable-item-checkbox"
                                                    checked={
                                                        selectedRows.length > 0 &&
                                                        selectedRows.length == normalizedData?.length
                                                    }
                                                    onChange={handleSelectAll}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column, i) => {
                                            if (column.hidden && column.hidden == true) return undefined;
                                            return (
                                                <TableCell
                                                    sx={{
                                                        ...(tableHeadStyles ?? tHeadStyles),
                                                        ...(column.sticky ? stickyStyle(column.sticky) : {}),
                                                        backgroundColor: "#F6F6F6",
                                                        zIndex: column.sticky ? 1000 : undefined,
                                                    }}
                                                    key={`column-${column.id}`}
                                                    align={column.align ?? "center"}
                                                >
                                                    {column.sort ? (
                                                        <TableSortLabel
                                                            active={(APIData ? APIData.sortBy : orderBy) === column.id}
                                                            direction={APIData ? APIData.sortOrder : orderAsc}
                                                            onClick={
                                                                APIData
                                                                    ? () => APIData.handleSort(column.id as any)
                                                                    : handleSort(column.id as any)
                                                            }
                                                        >
                                                            {column.label}
                                                        </TableSortLabel>
                                                    ) : (
                                                        column.label
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading
                                        ? loadingTable.map((item) => {
                                              return (
                                                  <TableRow key={`skeleton-${item}`}>
                                                      {loadingTableColumns.map((column, i) => {
                                                          const borderStyles = {
                                                              borderBottom: "none",
                                                              borderTop: i == 0 ? "none" : "1px solid #eaeaea",
                                                          };
                                                          return (
                                                              <TableCell key={`column-${column}`}>
                                                                  <Skeleton variant="text" />
                                                              </TableCell>
                                                          );
                                                      })}
                                                  </TableRow>
                                              );
                                          })
                                        : !isLoading && dataToMap && dataToMap?.length > 0
                                          ? dataToMap?.map((row: any, i: number) => {
                                                const borderStyles = {
                                                    borderBottom: "none",
                                                    borderTop: i == 0 ? "none" : "1px solid #eaeaea",
                                                };

                                                const idRepeated = dataToMap.filter((item) => item.id === row.id);
                                                const usesCollapse = collapseOn && row[collapseOn(row)];

                                                return (
                                                    <React.Fragment key={idRepeated ? `${row.id}-${i}` : row.id}>
                                                        {isNewDate &&
                                                            isNewDate(row, i, dataToMap) &&
                                                            separatorContent &&
                                                            separatorContent(row, i)}
                                                        <TableRow
                                                            className={`${
                                                                onRowClick ? "last-element-table-table-row" : ""
                                                            } 
                                                            ${usesCollapse ? "last-element-table-collapsed" : ""}
                                                            ${
                                                                selectedRows.includes(row.id)
                                                                    ? "last-element-table-selected"
                                                                    : ""
                                                            } ${rowClassName?.(row) ?? ""}`}
                                                            onClick={() =>
                                                                onRowClick
                                                                    ? onRowClick(
                                                                          row[onRowClickId ?? 0] ?? row.id,
                                                                          onRowClickId2 ? row[onRowClickId2 ?? 1] : "",
                                                                          row.type ?? "title",
                                                                          row,
                                                                      )
                                                                    : {}
                                                            }
                                                            key={i}
                                                            sx={{
                                                                backgroundColor: `${
                                                                    collapseOn && row[collapseOn(row)]
                                                                        ? collapseSelectedBackground
                                                                        : ""
                                                                }`,
                                                            }}
                                                        >
                                                            {selectable && (
                                                                <TableCell
                                                                    sx={{
                                                                        ...tBodyStyle,
                                                                        ...borderStyles,
                                                                        maxWidth: "42px",
                                                                    }}
                                                                    align="center"
                                                                    className="with-checkbox"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    key={`${i}-checkbox`}
                                                                >
                                                                    <StyledCheckbox
                                                                        className="last-element-table-selectable-item-checkbox"
                                                                        checked={selectedRows.includes(row.id)}
                                                                        onChange={(e) => handleSelectRow(e, row.id)}
                                                                    />
                                                                </TableCell>
                                                            )}
                                                            {columns.map((column, j) => {
                                                                const avatar =
                                                                    row[avatarSourceValue ?? "avatarUrl"] ?? "";
                                                                const value = column.format
                                                                    ? column.format(
                                                                          row[column.id],
                                                                          row.id ?? undefined,
                                                                          row,
                                                                      )
                                                                    : row[column.id];
                                                                const outOf = column.outOf
                                                                    ? column.format
                                                                        ? column.format(row[column.outOf])
                                                                        : row[column.outOf]
                                                                    : false;
                                                                const subtitle = column.subtitle
                                                                    ? row[column.subtitle]
                                                                    : false;
                                                                const showAvatar =
                                                                    column.avatar || column.avatar == undefined;
                                                                const minWidth = column.minWidth
                                                                    ? { minWidth: column.minWidth }
                                                                    : {};
                                                                const opacity =
                                                                    column.opacityReducer && row[column.opacityReducer]
                                                                        ? { opacity: "0.5", cursor: "not-allowed" }
                                                                        : {};
                                                                const maxWidth = column.maxWidth
                                                                    ? { maxWidth: `${column.maxWidth} !important` }
                                                                    : {};
                                                                const stickyColumn = column.sticky
                                                                    ? stickyStyle(column.sticky)
                                                                    : {};
                                                                const extraStyle = {
                                                                    ...stickyColumn,
                                                                    ...minWidth,
                                                                    ...maxWidth,
                                                                    ...opacity,
                                                                };

                                                                if (column.hidden && column.hidden === true) {
                                                                    return undefined;
                                                                }

                                                                if (
                                                                    (column.id == "name" || column.id == "company") &&
                                                                    showAvatar == true
                                                                )
                                                                    return (
                                                                        <TableCell
                                                                            sx={{
                                                                                ...(tableBodyStyles ?? tBodyStyle),
                                                                                ...borderStyles,
                                                                                ...(column.sticky ? stickyStyle : {}),
                                                                                ...maxWidth,
                                                                            }}
                                                                            key={j}
                                                                            onClick={(e) =>
                                                                                column.stopPropagation
                                                                                    ? e.stopPropagation()
                                                                                    : {}
                                                                            }
                                                                        >
                                                                            <div
                                                                                className="last-element-table-cell"
                                                                                style={{
                                                                                    justifyContent:
                                                                                        column.align ?? "center",
                                                                                }}
                                                                            >
                                                                                <Avatar
                                                                                    variant={`${
                                                                                        column.avatarType ?? "rounded"
                                                                                    }`}
                                                                                    src={avatar ?? "#"}
                                                                                    sx={{
                                                                                        width: 24,
                                                                                        height: 24,
                                                                                        backgroundColor: "#FFF2F1",
                                                                                        color: "black",
                                                                                        fontSize: 12,
                                                                                    }}
                                                                                    imgProps={{
                                                                                        sx: { objectFit: "cover" },
                                                                                    }}
                                                                                >
                                                                                    {value?.slice(0, 1).toUpperCase()}
                                                                                </Avatar>
                                                                                <span
                                                                                    title={value}
                                                                                    className="last-element-table-cell-text"
                                                                                >
                                                                                    {value}
                                                                                </span>
                                                                            </div>
                                                                        </TableCell>
                                                                    );

                                                                return (
                                                                    <TableCell
                                                                        align={column.align ?? "center"}
                                                                        sx={{
                                                                            ...(tableBodyStyles ?? tBodyStyle),
                                                                            ...borderStyles,
                                                                            ...extraStyle,
                                                                        }}
                                                                        key={j}
                                                                        onClick={(e) =>
                                                                            column.stopPropagation
                                                                                ? e.stopPropagation()
                                                                                : {}
                                                                        }
                                                                    >
                                                                        <>
                                                                            <span
                                                                                title={
                                                                                    typeof value == "string"
                                                                                        ? value
                                                                                        : ""
                                                                                }
                                                                                className="last-element-table-cell-text"
                                                                            >
                                                                                {value}
                                                                            </span>
                                                                            {column.outOf && (
                                                                                <span
                                                                                    title={outOf}
                                                                                    className="last-element-table-cell-text-out-of"
                                                                                >
                                                                                    /{outOf}
                                                                                </span>
                                                                            )}
                                                                            {column.subtitle && (
                                                                                <div
                                                                                    title={subtitle}
                                                                                    className="last-element-table-cell-text-subtitle"
                                                                                >
                                                                                    {subtitle}
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>
                                                        {collapseOn && collapseContent && row[collapseOn(row)] && (
                                                            <TableRow
                                                                className={`last-element-table-collapsed ${
                                                                    selectedRows.includes(row.id)
                                                                        ? "last-element-table-selected"
                                                                        : ""
                                                                }`}
                                                                key={`${i}-collapse`}
                                                                sx={{
                                                                    backgroundColor: `${
                                                                        collapseOn &&
                                                                        row[collapseOn(row)] &&
                                                                        collapseContentBackground
                                                                            ? collapseContentBackground
                                                                            : ""
                                                                    }`,
                                                                }}
                                                            >
                                                                <TableCell
                                                                    style={{ padding: 0, border: "none" }}
                                                                    colSpan={columns.length + (selectable ? 1 : 0)}
                                                                >
                                                                    <Collapse in={true} timeout={500} unmountOnExit>
                                                                        {collapseContent(row)}
                                                                    </Collapse>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                          : dataType && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={columns.length + (selectable ? 1 : 0)}
                                                        sx={{ borderBottom: "none" }}
                                                        className="empty-state-cell"
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                width: "100%",
                                                                height: "40vh",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <EmptyState
                                                                type={(dataType as any) ?? "generic"}
                                                                handleAction={emptyStateAction}
                                                                portalType={portalType}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                </TableBody>

                                {!isPaginated && !bottomFooterContent && !allowInfiniteScroll && (
                                    <TableFooter>
                                        <TableRow className="show-all">
                                            <TableCell
                                                sx={{
                                                    border: "none",
                                                    cursor: "pointer",
                                                    borderTop: "1px solid #eaeaea",
                                                }}
                                                onClick={onShowAll}
                                                colSpan={columns.length + (selectable ? 1 : 0)}
                                            >
                                                <div>
                                                    <ActionArrowRight />
                                                    {type ?? maxRows !== max
                                                        ? t("BPSettings.SeeLess")
                                                        : t("BPSettings.SeeAll")}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                )}
                            </Table>
                        </TableContainer>
                    </ScrollShadowWrapper>

                    {isPaginated &&
                        (APIData ? (
                            <TablePagination
                                component={() => (
                                    <Pagination
                                        rowsPerPageOptions={[10, 25, 100]}
                                        rowsPerPage={APIData.limitPagination}
                                        currentPageSize={(normalizedData ?? []).length}
                                        page={APIData.page}
                                        onPageChange={APIData.handleChangePage}
                                        onRowsPerPageChange={APIData.handleChangeLimit}
                                        type={dataType!}
                                        count={dataCount ?? (normalizedData?.length || 0)}
                                        sticky={stickyFooter}
                                        portalType={portalType}
                                    />
                                )}
                                count={(normalizedData && normalizedData.length) || 0}
                                rowsPerPage={APIData.limitPagination}
                                page={APIData.page}
                                onPageChange={APIData.handleChangePage}
                            />
                        ) : (
                            <TablePagination
                                component={() => (
                                    <Pagination
                                        rowsPerPageOptions={[10, 25, 100]}
                                        rowsPerPage={rowsPerPage}
                                        currentPageSize={(normalizedData || []).length}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        type={dataType!}
                                        count={dataCount ?? (normalizedData?.length || 0)}
                                        sticky={stickyFooter}
                                        portalType={portalType}
                                    />
                                )}
                                count={(normalizedData && normalizedData.length) || 0}
                                rowsPerPageOptions={[10, 25, 100]}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                            />
                        ))}

                    {bottomFooterContent != undefined && bottomFooterContent}
                </Paper>
            )}
        </div>
    );
});

export default React.memo(LastElementTable);
